/**
 * Настройки приложения, приходящие с нашего сервера.
 *
 * ЗАЧЕМ. Раньше, чтобы убрать из приложения одну кнопку, нужна была новая
 * сборка и новая проверка в App Store — неделя ожидания и лишний повод к нам
 * присмотреться. Теперь поведение задаётся с сервера: выключил тумблер в
 * админке — через минуту у всех учеников пропало.
 *
 * ⚠️ ГЛАВНОЕ ПРАВИЛО: настройка НЕ МОЖЕТ ОТКРЫТЬ то, что закрыто в коде.
 * Значение с сервера складывается с кодовой проверкой через «И», никогда через
 * «ИЛИ». Тумблер, способный показать платёжный экран после одобрения проверки,
 * — это правило Apple 2.3.1 «скрытая функциональность»: за него закрывают
 * аккаунт разработчика целиком, а не отклоняют сборку.
 *
 * Обычных возможностей правило не касается: видео, спикинг, тренажёры
 * переключаются в обе стороны сколько угодно раз.
 */

/**
 * Умолчания — это страховка, а не «красивые значения». Они равны сегодняшнему
 * поведению платформы: если сервер настроек молчит, приложение ведёт себя ровно
 * так, как вело до появления настроек.
 *
 * Видео и разговорные комнаты выключены: сервер видеоконференций не отвечает
 * (решение владельца от 18.08.2026).
 */
const DEFAULTS = {
  lessons: true,
  vocab: true,
  cefr: true,
  ielts: true,
  // Отдельные тренажёры (01.09.2026): каждый закрывается своим тумблером.
  // Общий `ielts` остаётся ради тарифов, заведённых раньше, — на сервере он
  // означает «все тренажёры».
  ielts_reading: true,
  ielts_listening: true,
  ielts_writing: true,
  ielts_speaking: true,
  mock: true,
  sprint: true,
  speaking_ai: true,
  writing_ai: true,
  chat: true,
  attendance: true,
  video: false,
  /**
   * ⚠️ ПРОБА 58, ТОЛЬКО ВЕТКА video-native-webview (16.09.2026, техдолг п. 232). В приложении первый
   * видеоблок урока играет в отдельном нативном WKWebView поверх карточки (`lib/inlineVideoNative.ts`).
   * Выключено — прежнее окно с «Готово». Выключить с сервера: `features.video_native = false`.
   */
  video_native: true,
  /**
   * Гостевой режим ПРИЛОЖЕНИЯ (решение владельца 14.09.2026, договор guest-mode.md): без входа —
   * гостевая главная со словом дня, курсами и словарём. Выключен с сервера — гость в приложении
   * попадает на экран входа. На сайт не действует: там гостевых экранов нет.
   */
  guest_mode: true,
  /**
   * Поиск слова у гостя. Ручки `GET /vocab/public/lookup` пока нет (задача полосе B): выключено —
   * поле поиска ведёт на регистрацию. Включать с сервера только после выката ручки.
   */
  guest_vocab_search: false,
  /**
   * Главная страница САЙТА (academy.onebilim.kz до входа).
   *
   * ⚠️ ВЫКЛЮЧАТЕЛЬ ДЕЙСТВУЕТ ТОЛЬКО В ВЕБЕ. В приложении лендинг — это первый
   * экран, и пустым он быть не может ни при каких настройках: пустое приложение
   * Apple разворачивает по правилу 4.2. Поэтому проверка стоит рядом с
   * «мы в приложении?», а не вместо неё.
   *
   * Содержимое при выключении НИКУДА НЕ ДЕВАЕТСЯ — блоки лежат в сборке, вернуть
   * их можно тем же тумблером, без выката.
   */
  landing: true,
  /**
   * Входы в свободный AI speaking (решение владельца D22, 11.09.2026).
   *
   * ⚠️ ВЫКЛЮЧЕНО УМОЛЧАНИЕМ, А НЕ ТАРИФОМ. Тарифные `speaking_ai` и
   * `ielts_speaking` открывают ещё и речь в Mock — их трогать нельзя. Этот ключ
   * только прячет входы ученика; разбор — `lib/aiEntries.ts`. Вернуть можно с
   * сервера, ключом `features.show_ai_speaking`.
   */
  show_ai_speaking: false,
  /**
   * ИИ-тренажёры listening/reading/writing и спринт (решение владельца D26,
   * 11.09.2026): задания ненадёжны, закрыты до переделки. Каталог полных тестов
   * и Mock не закрываются. Вернуть — `features.show_ai_trainers` с сервера.
   *
   * ⚠️ С 12.10.2026 ЭТОТ КОД КЛЮЧ НЕ ЧИТАЕТ: тренажёры закрываются по навыкам
   * (`lib/aiSkills.ts`, docs/CONTRACTS/ai-skills.md). Ключ остаётся ради
   * приложения 1.1.1, которое уже в магазине и читает его своей сборкой.
   */
  show_ai_trainers: false,
  /**
   * «Удалить профиль» на САЙТЕ (D37, решение владельца 12.09.2026): ученики
   * удалялись, не могли войти и заводили новую учётку — дубли. В приложении из
   * магазина кнопка есть всегда (Apple 5.1.1(v)), этот ключ её там не прячет —
   * разбор в `lib/selfDelete.ts`. Вернуть на сайте — `features.show_self_delete`.
   */
  show_self_delete: false,
  /**
   * Писать запись кусками и складывать их в хранилище браузера, чтобы
   * незаконченная запись пережила перезагрузку страницы (12.09.2026).
   *
   * ⚠️ УМОЛЧАНИЕ — ВКЛЮЧЕНО, и это осознанно: куски меняют способ записи у всех,
   * поэтому их сперва замерили живьём — Safari 26.5.2 на маке, Chrome 151 и
   * настоящий iPhone (iOS 18.7): длина файла не теряется, а куски без концовки
   * читаются. Флаг оставлен, чтобы выключить с сервера, если Android удивит.
   */
  recordChunks: true,
  /**
   * Мягкая сверка набранного слова в тренажёре карточек — буквы-двойники и артикль
   * (договор vocab-answer-fold.md, 12.09.2026), разбор в `lib/wordAnswer.ts`.
   *
   * ⚠️ ВЫКЛЮЧЕНО, ПОКА НА ПРОДЕ СТАРЫЙ `vocab-service`. Бэкенд первым (договор §3):
   * фронт с мягкой сверкой при старом сервере дал бы галочку на экране и ошибку в
   * базе. Включить после прода vocab — `features.vocab_answer_fold = true`, без сборки.
   */
  vocab_answer_fold: false,
  /**
   * Кнопка «Пауза» в записи голоса (слово владельца 12.09.2026).
   *
   * ⚠️ ВЫКЛЮЧЕНО: после её появления 11.09 доля обрывов записи на Mac-Safari и iPad
   * выросла с 3,6 до 18,3 % (docs/CHECK-2026-09-12-RECORD-TRUNC.md). Имя ключа — как
   * назвал владелец, не snake_case. Вернуть — `features.recordPause = true`, без сборки.
   */
  recordPause: false,
  /**
   * Запись голоса шлёт `client_id` (договор `audio-upload.md`, 02.10.2026): повтор — не дубль, опоздавшая
   * запись получает отказ вместо того, чтобы вернуть принятую работу на проверку, сервер видит недошедшее.
   * Сайт и приложение, от тумблера ученика не зависит.
   *
   * ⚠️ В СБОРКЕ ВЫКЛЮЧЕНО: обновлений мимо магазина нет, и новое обязано гаситься с сервера. Включить после
   * стенда — `features.record_client_id = true`. Читается в момент отправки (`lib/offlineRecords.ts`).
   */
  record_client_id: false,
  /**
   * Серверный выключатель «Записи без связи» в приложении: запись голоса файлом на телефоне. Выключен —
   * тумблера в настройках нет вовсе, запись идёт как в 1.1.0. Включён — решает тумблер ученика (он по
   * умолчанию выключен). `features.offline_records = true`, без сборки.
   */
  offline_records: false,
  speaking_peers: false,
  live: false,
  /**
   * Платёжные экраны. Ученик не видит их нигде и никогда — решение владельца от
   * 31.08.2026. Доступ выдаёт администратор, тариф ученику не показывается.
   */
  payments: false,
} as const;

export type FeatureKey = keyof typeof DEFAULTS;

const CACHE_KEY = 'onebilim.appConfig.v1';

/**
 * Тот же адрес, что у остальных запросов.
 *
 * ⚠️ НЕ ЗАМЕНЯТЬ НА '/api/v1'. В приложении из магазина страница открывается с
 * `capacitor://localhost`, и относительный адрес ведёт внутрь самого
 * приложения, где никакого сервера нет. Настройки просто не загрузились бы, и
 * это выглядело бы как «в мобилке ничего не работает».
 */
const BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api/v1';

type ConfigDocument = {
  features?: Record<string, unknown>;
  nav?: unknown[];
  limits?: Record<string, unknown>;
  texts?: Record<string, unknown>;
  minVersion?: Record<string, unknown>;
  latestVersion?: Record<string, unknown>;
};

let doc: ConfigDocument = readCache();

/**
 * Кто ждёт приезда настроек.
 *
 * 🔴 БЕЗ ЭТОГО НАСТРОЙКИ НЕ ДЕЙСТВУЮТ НА ПЕРВОМ ЭКРАНЕ. Документ приезжает
 * запросом, то есть ПОСЛЕ первой отрисовки. Экраны, нарисованные до его
 * приезда, остаются на умолчаниях НАВСЕГДА: перерисовать их было нечему.
 *
 * Пока настройки читали внутренние экраны, это не всплывало — туда человек
 * попадает уже после загрузки. Всплыло на главной странице: тумблер выключен на
 * сервере, а страница показывается как ни в чём не бывало (05.09.2026).
 */
let version = 0;
const configListeners = new Set<() => void>();

/** Подписаться на приезд настроек. Возвращает отписку. */
export function subscribeAppConfig(listener: () => void): () => void {
  configListeners.add(listener);
  return () => configListeners.delete(listener);
}

/** Счётчик обновлений — для перерисовки через `useSyncExternalStore`. */
export function getAppConfigVersion(): number {
  return version;
}

function readCache(): ConfigDocument {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ConfigDocument) : {};
  } catch {
    // Приватный режим, забитое хранилище, испорченная запись — работаем на
    // умолчаниях. Настройки не тот повод, чтобы падать.
    return {};
  }
}

/**
 * Забирает настройки с сервера. Ошибку наружу не отдаёт: приложение уже
 * нарисовано на умолчаниях, и человеку незачем знать про служебный запрос.
 */
export async function loadAppConfig(): Promise<void> {
  try {
    const res = await fetch(`${BASE_PATH}/app-config`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;

    const body = (await res.json()) as { data?: ConfigDocument } | ConfigDocument;
    const next = (body as { data?: ConfigDocument })?.data ?? (body as ConfigDocument);
    if (!next || typeof next !== 'object') return;

      doc = next;
      // ⚠️ Сообщаем подписчикам: без этого экраны, нарисованные ДО приезда
      // настроек, остаются на умолчаниях навсегда.
      version += 1;
      configListeners.forEach((l) => l());
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    } catch {
      /* хранилище недоступно — переживём, настройки просто не сохранятся */
    }
  } catch {
    /* сети нет — остаёмся на прошлом ответе и умолчаниях */
  }
}

/**
 * Что нельзя открыть настройкой ни при каких условиях.
 *
 * ⚠️ Здесь только платёжное. Соблазн дописать сюда `video` («оно же выключено»)
 * — ошибка: тогда видео нельзя будет вернуть с сервера, а ради этого всё и
 * делается. Выключенность видео задаётся умолчанием, а не кодовым запретом.
 */
function codeAllows(key: FeatureKey): boolean {
  return key !== 'payments';
}

/** Работает ли возможность прямо сейчас. */
export function isFeatureOn(key: FeatureKey | string): boolean {
  const known = key as FeatureKey;
  const fallback = DEFAULTS[known];
  if (fallback === undefined) return false; // о таком ключе мы не знаем — значит нет

  const fromServer = doc.features?.[known];
  const value = typeof fromServer === 'boolean' ? fromServer : fallback;

  return value && codeAllows(known);
}
/** Число из настроек: лимит устройств, длительность тренажёра и подобное. */
export function getLimit(name: string, fallback: number): number {
  const value = doc.limits?.[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Порядок и видимость блоков лендинга, если сервер их задал.
 *
 * Разбор и защита от пустой страницы — в `resolveLandingLayout`: здесь только
 * достаём присланное, ничего не решая.
 */
export function getLandingBlocks(): { key: string; visible?: boolean }[] | undefined {
  const raw = (doc as { landing?: { blocks?: unknown } }).landing?.blocks;
  return Array.isArray(raw) ? (raw as { key: string; visible?: boolean }[]) : undefined;
}

/**
 * Минимально допустимая версия приложения для этой платформы.
 *
 * ⚠️ ТОЛЬКО ВНУТРИ ПРИЛОЖЕНИЯ. На сайте обновлять нечего — там всегда свежая
 * страница, и просить обновиться было бы бессмыслицей.
 */
export function getMinVersion(platform: 'ios' | 'android'): string | undefined {
  const v = doc.minVersion?.[platform];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/**
 * Последняя вышедшая в магазине версия (15.10.2026): ниже неё — мягкая плашка
 * «Вышла новая версия» с «Обновить» и «Позже». Админ ставит её руками, когда
 * версия РЕАЛЬНО вышла в App Store. Пусто или старая служба без раздела — плашки нет.
 */
export function getLatestVersion(platform: 'ios' | 'android'): string | undefined {
  const v = doc.latestVersion?.[platform];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** Умолчание и пределы «Позже» на плашке, в днях (решение владельца — 3; сервер держит те же 1–30). */
export const UPDATE_REMINDER_DAYS = { fallback: 3, min: 1, max: 30 } as const;

/** Сколько дней молчит плашка после «Позже». Вне пределов или не число — умолчание. */
export function getUpdateReminderDays(): number {
  const v = doc.limits?.update_reminder_days;
  const { fallback, min, max } = UPDATE_REMINDER_DAYS;
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max ? v : fallback;
}

/**
 * Поколение сессий. Увеличили на сервере — все выходят один раз.
 * Нужно, когда прежний вход перестаёт работать правильно.
 */
export function getSessionEpoch(): number | undefined {
  const v = doc.limits?.session_epoch;
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Все правки текстов, присланные сервером: ключ перевода → язык → строка. */
export function getTextOverrides(): Record<string, Record<string, string>> {
  const texts = doc.texts;
  return (texts && typeof texts === 'object' ? texts : {}) as Record<string, Record<string, string>>;
}

/** Объявление ученикам на его языке, если оно задано. */
export function getText(name: string, lang: string): string {
  const entry = doc.texts?.[name];
  if (!entry || typeof entry !== 'object') return '';
  const value = (entry as Record<string, unknown>)[lang];
  return typeof value === 'string' ? value : '';
}

/** Только для тестов: забыть загруженное, как после перезапуска приложения. */
export function __resetForTests(): void {
  doc = readCache();
}
